import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export interface UpgradePayload {
  errorCode?: string;
  message?: string;
  currentPlan?: string;
  requiredPlan?: string;
  feature?: string;
  description?: string;
}

interface UpgradeModalContextValue {
  openUpgradeModal: (payload?: UpgradePayload) => void;
}

const UpgradeModalContext = createContext<UpgradeModalContextValue>({
  openUpgradeModal: () => { },
});

export const useUpgradeModal = () => useContext(UpgradeModalContext);

export const UPGRADE_EVENT = 'saas:upgrade-required';

export const emitUpgradeRequired = (payload: UpgradePayload = {}) => {
  window.dispatchEvent(new CustomEvent(UPGRADE_EVENT, { detail: payload }));
};

interface Props {
  children: React.ReactNode;
  renderModal: (open: boolean, payload: UpgradePayload, onClose: () => void) => React.ReactNode;
}

export const UpgradeModalProvider: React.FC<Props> = ({ children, renderModal }) => {
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState<UpgradePayload>({});

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<UpgradePayload>).detail ?? {};
      setPayload(detail);
      setOpen(true);
    };
    window.addEventListener(UPGRADE_EVENT, handler);
    return () => window.removeEventListener(UPGRADE_EVENT, handler);
  }, []);

  const openUpgradeModal = useCallback((p: UpgradePayload = {}) => {
    setPayload(p);
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => setOpen(false), []);

  return (
    <UpgradeModalContext.Provider value={{ openUpgradeModal }}>
      {children}
      {renderModal(open, payload, handleClose)}
    </UpgradeModalContext.Provider>
  );
};
